import { useEffect, useState } from 'react';
import organizationService from '../services/organizationService';
import { buildServerUrl } from '../lib/config';

export interface OrgBranding {
  organization_id: number;
  name: string;
  logo_white_url: string | null;
  logo_black_url: string | null;
  company_name: string;
  address: string;
  phone: string;
  email: string;
  legal: string;
}

/** Prefix relative storage paths with the server base; leave absolute URLs untouched. */
const resolveAsset = (url: string | null): string | null => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return buildServerUrl(url);
};

// Cache branding for the session so every layout doesn't re-fetch it.
let cached: OrgBranding | null | undefined;
let inflight: Promise<OrgBranding | null> | null = null;

async function fetchBranding(): Promise<OrgBranding | null> {
  const res = await organizationService.getBranding();
  const data = res?.data;
  if (!data) return null;
  return {
    ...data,
    logo_white_url: resolveAsset(data.logo_white_url),
    logo_black_url: resolveAsset(data.logo_black_url),
  } as OrgBranding;
}

/** Load footer branding for the current user's organization. */
export function useOrgBranding() {
  const [branding, setBranding] = useState<OrgBranding | null>(cached ?? null);
  const [loading, setLoading] = useState(cached === undefined);

  useEffect(() => {
    let active = true;
    if (cached !== undefined) {
      setBranding(cached);
      setLoading(false);
      return;
    }
    if (!inflight) {
      inflight = fetchBranding()
        .then((b) => {
          cached = b;
          return b;
        })
        .catch(() => {
          cached = null;
          return null;
        })
        .finally(() => {
          inflight = null;
        });
    }
    inflight.then((b) => {
      if (active) {
        setBranding(b);
        setLoading(false);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  return { branding, loading };
}

/** Clear the cached branding (call after the org admin edits footer settings). */
export function invalidateOrgBranding() {
  cached = undefined;
  inflight = null;
}
